/**
 * Frase para a pessoa a partir de um erro técnico (auditoria de design, 11/10/2026, DO-01).
 *
 * ⚠️ O que acontecia: com a API fora do ar, as telas mostravam o CÓDIGO do servidor ("mock_erro_500: …", "http_500",
 * "erro_interno: …"), algumas ficavam em "Carregando…" para sempre (Clientes, Vínculos, Usuários, Configurações) e outras
 * escreviam "Nenhum circuito cadastrado" AO LADO do erro: o operador lia "não há nada" quando só a API tinha caído.
 * Regras que ficam:
 *  - erro de carga NUNCA vira "vazio", "zero" nem "Carregando…" eterno; vira esta frase, com "Tentar de novo";
 *  - o código técnico NÃO é a mensagem: vai no `title` (e no log), para o time de suporte;
 *  - texto que já é frase em português (regra de negócio devolvida pelo servidor) passa como está.
 */

const PARECE_CODIGO = /^[a-z0-9]+(?:_[a-z0-9]+)*(?::|$)/; // só minúsculas e _ (frase com maiúscula, espaço ou acento não é código); "http_500", "erro_interno: …", "mock_erro_500: …"
const PARECE_REDE = /failed to fetch|networkerror|load failed|network request failed|timeout|timed out|abort/i;

export function ehCodigoTecnico(msg: string): boolean {
  return PARECE_CODIGO.test(msg.trim()) || PARECE_REDE.test(msg);
}

/** Frase em português para um erro técnico; devolve a própria mensagem se ela já for uma frase. */
export function fraseDoErro(msg: string, status?: number): string {
  const m = msg.trim();
  const http = status ?? Number(/^http_(\d{3})/.exec(m)?.[1] ?? /_(\d{3})(?::|$)/.exec(m)?.[1] ?? NaN);
  if (!ehCodigoTecnico(m) && !Number.isFinite(http)) return m;
  if (http === 401 || http === 403 || /nao_autorizado|forbidden|permiss/i.test(m)) {
    return "Seu perfil não tem acesso a esta informação. Se precisar dela, peça a um administrador.";
  }
  if (http === 404 || /nao_encontrad/i.test(m)) return "Não encontramos o que foi pedido. Ele pode ter sido removido.";
  if (http === 429 || /rate_limit|too many/i.test(m)) return "Muitos pedidos em pouco tempo. Aguarde alguns segundos e tente de novo.";
  if ((Number.isFinite(http) && http >= 500) || /erro_interno|internal|server_error|indisponivel|unavailable/i.test(m)) return "O sistema não respondeu agora. Tente de novo em instantes.";
  if (PARECE_REDE.test(m)) return "Não foi possível falar com o sistema. Confira a conexão e tente de novo.";
  return "Não foi possível concluir. Tente de novo; se continuar, avise o time e informe o código do detalhe (passe o mouse sobre a mensagem).";
}

/** Para um `unknown` capturado em catch/query: frase pronta. */
export function mensagemDoErro(e: unknown): string {
  if (e instanceof Error) {
    const status = (e as { status?: unknown }).status;
    return fraseDoErro(e.message, typeof status === "number" ? status : undefined);
  }
  return fraseDoErro(String(e));
}
